import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import type { AccountFilterOption } from '../components/views/account/ManageAccountsSearchBar';
import {
  SkinTypeDefStorage,
  SKIN_TYPE_DEF_SCHEMA_VERSION,
  type SkinSettingDto,
} from '../storage/SkinTypeDefStorage';
import { ensureBundledSkinAssets } from '../services/skin/resolveSkinChannelInfo';

const toOptions = (lists: SkinSettingDto[][]): AccountFilterOption[] => {
  const byValue = new Map<string, AccountFilterOption>();
  lists.flat().forEach((entry) => {
    const value = (entry.value || '').trim();
    if (value && !byValue.has(value)) {
      byValue.set(value, { value, label: (entry.name || '').trim() || value });
    }
  });
  return Array.from(byValue.values());
};

/**
 * Account Group / Processor filter lists for the account search bars. They come from the synced
 * SkinTypeDefs (every stored skin, de-duplicated by value).
 */
export const useAccountFilterOptions = (storage?: any) => {
  const [accountGroupOptions, setAccountGroupOptions] = useState<AccountFilterOption[]>([]);
  const [billpayProcessorOptions, setBillpayProcessorOptions] = useState<AccountFilterOption[]>([]);

  useEffect(() => {
    if (!storage) {
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const skinDefStorage = new SkinTypeDefStorage(storage);
        let defs = await skinDefStorage.getAllSkinTypeDefs();
        const isStale = defs.length === 0 || defs.some(def => (def.schemaVersion ?? 0) < SKIN_TYPE_DEF_SCHEMA_VERSION);
        if (isStale && Platform.OS === 'web') {
          try {
            await ensureBundledSkinAssets(storage);
            defs = await skinDefStorage.getAllSkinTypeDefs();
          } catch (bundleError) {
            console.warn('useAccountFilterOptions: failed to load bundled skin assets', bundleError);
          }
        }
        if (cancelled) {
          return;
        }
        setAccountGroupOptions(toOptions(defs.map(def => def.accountGroups ?? [])));
        setBillpayProcessorOptions(toOptions(defs.map(def => def.billpayProcessors ?? [])));
      } catch (loadError) {
        console.warn('useAccountFilterOptions: failed to load account filter lists', loadError);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [storage]);

  return { accountGroupOptions, billpayProcessorOptions };
};
